/**
 * Assets Tab — Asset accounts table with add/edit/remove in edit mode.
 * Consumes API shape: { assets: AssetViewModel[] }
 */
import { Controller, useWatch, useFormContext } from 'react-hook-form';
import { Box, Button, Card, CardContent, Chip, Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import LockIcon from '@mui/icons-material/Lock';
import CurrencyInput from '../../../components/shared/CurrencyInput';
import SectionHeader from '../../../components/shared/SectionHeader';

import { fmtDollar as fmt } from '../../../utils/formatters';

const ASSET_TYPES = [
  { value: 'CheckingAccount', label: 'Checking' },
  { value: 'SavingsAccount', label: 'Savings' },
  { value: 'MoneyMarketFund', label: 'Money Market' },
  { value: 'CertificateOfDepositTimeDeposit', label: 'CD' },
  { value: 'MutualFund', label: 'Mutual Fund' },
  { value: 'Stock', label: 'Stock' },
  { value: 'Bond', label: 'Bond' },
  { value: 'RetirementFund', label: 'Retirement' },
  { value: 'TrustAccount', label: 'Trust' },
  { value: 'GiftOfCash', label: 'Gift of Cash' },
  { value: 'Other', label: 'Other' },
];

const formatMonths = (v) => {
  if (v == null) return null;
  const num = Number(v);
  if (!Number.isFinite(num)) return null;
  // One decimal looks cleaner than e.g. "12.000000" when Vesta returns a high-precision decimal,
  // but drop the ".0" when the value is whole.
  return Number.isInteger(num) ? `${num}` : num.toFixed(1);
};

const AssetsTab = ({ loanDetail, isEditMode = false }) => {
  const { control, setValue, getValues } = useFormContext();
  const watchedAssets = useWatch({ control, name: 'assets' });
  /**
   * Vesta marks `monthsOfReservesAvailable` as read-only — they compute it from the loan's
   * actual reserve accounts + housing expenses. The only writable months-reserves field is
   * `monthsOfReservesAvailableEstimate`, so that's what the broker edits. When the broker
   * leaves the estimate blank, the pricing engine and display fall back to nothing if Vesta
   * hasn't computed `monthsOfReservesAvailable` either.
   */
  const watchedMonthsReservesEstimate = useWatch({
    control,
    name: 'loan.monthsOfReservesAvailableEstimate',
  });

  if (!loanDetail) return null;
  const assets = isEditMode ? (watchedAssets || []) : (loanDetail.assets || []);
  const totalAssets = assets.reduce((s, a) => s + (a.assetValue || 0), 0);

  const loanInfo = loanDetail.loan || {};
  // Display prefers the Vesta-computed value (`monthsOfReservesAvailable`) and falls back to the
  // broker-supplied estimate (`monthsOfReservesAvailableEstimate`) when Vesta hasn't computed one.
  const monthsReservesComputed = loanInfo.monthsOfReservesAvailable;
  const monthsReservesEstimateApi = loanInfo.monthsOfReservesAvailableEstimate;
  const monthsReservesEstimate = isEditMode
    ? (watchedMonthsReservesEstimate ?? monthsReservesEstimateApi)
    : monthsReservesEstimateApi;
  const monthsReservesEffective = monthsReservesComputed ?? monthsReservesEstimate;
  const monthsReservesDisplay = formatMonths(monthsReservesEffective);
  const monthsReservesIsEstimate =
    (monthsReservesComputed == null || monthsReservesComputed === '')
    && monthsReservesEstimate != null
    && monthsReservesEstimate !== '';

  const updateAssets = (updated) => {
    setValue('assets', updated, { shouldDirty: true });
  };

  const showSidebar = !isEditMode && assets.length > 0;

  // Group assets by type for sidebar
  const assetsByType = assets.reduce((acc, a) => {
    const typeLabel = ASSET_TYPES.find(t => t.value === a.assetType)?.label || a.assetType || 'Other';
    acc[typeLabel] = (acc[typeLabel] || 0) + (a.assetValue || 0);
    return acc;
  }, {});
  const assetTypeEntries = Object.entries(assetsByType).sort((a, b) => b[1] - a[1]);

  const assetSidebar = showSidebar ? (
    <Box sx={{ position: 'sticky', top: 16 }}>
      <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <SectionHeader title="Asset Summary" icon={<SummarizeIcon fontSize="small" />} sx={{ mb: 1 }} />

          {/* Total */}
          <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">Total Assets</Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">{fmt(totalAssets)}</Typography>
            <Typography variant="caption" color="text.secondary">{assets.length} account{assets.length !== 1 ? 's' : ''}</Typography>
          </Box>

          {/* Breakdown by type */}
          <Box sx={{ pl: 0.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>By Type</Typography>
            {assetTypeEntries.map(([type, value]) => (
              <Stack key={type} direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">{type}</Typography>
                <Typography variant="caption" fontWeight={600}>{fmt(value)}</Typography>
              </Stack>
            ))}
          </Box>

          {/* Top accounts */}
          {assets.length > 0 && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider', pl: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>Accounts</Typography>
              {assets.slice().sort((a, b) => (b.assetValue || 0) - (a.assetValue || 0)).map((a, i) => (
                <Stack key={a.assetId || i} direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <AccountBalanceIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>{a.institutionName || ASSET_TYPES.find(t => t.value === a.assetType)?.label || 'Account'}</Typography>
                  </Stack>
                  <Typography variant="caption" fontWeight={600}>{fmt(a.assetValue)}</Typography>
                </Stack>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  ) : null;

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left column: Assets detail */}
        <Grid size={{ xs: 12, lg: showSidebar ? 8 : 12 }}>

      {/* Reserves summary. monthsOfReservesAvailable is the primary, editable Vesta value and
          drives the pricing engine. monthsOfReservesAvailableEstimate is a read-only fallback
          surfaced only when the primary value is blank. */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap">
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SavingsOutlinedIcon color="primary" />
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Months of Reserves Available
                  </Typography>
                  {!isEditMode && (
                    <LockIcon
                      sx={{ fontSize: 12, color: 'text.disabled' }}
                      titleAccess="Read-only outside edit mode"
                    />
                  )}
                </Stack>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="h6" fontWeight={700}>
                    {monthsReservesDisplay != null ? `${monthsReservesDisplay} mo` : '—'}
                  </Typography>
                  {monthsReservesIsEstimate && (
                    <Chip
                      size="small"
                      label="Estimate"
                      variant="outlined"
                      title="Falling back to monthsOfReservesAvailableEstimate because the primary value is blank"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            {isEditMode ? (
              <Stack direction="column" spacing={0.25} sx={{ minWidth: 220 }}>
                <Controller
                  name="loan.monthsOfReservesAvailableEstimate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Months of Reserves (Estimate)"
                      size="small"
                      type="number"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          field.onChange(null, { shouldDirty: true });
                          return;
                        }
                        const n = Number(v);
                        field.onChange(Number.isFinite(n) ? n : null, { shouldDirty: true });
                      }}
                      onBlur={field.onBlur}
                      slotProps={{
                        htmlInput: { step: '0.1', min: '0', max: '120' },
                        input: { endAdornment: <Typography variant="caption" color="text.secondary">mo</Typography> },
                      }}
                      placeholder={
                        monthsReservesComputed != null
                          ? `Vesta computed: ${formatMonths(monthsReservesComputed)}`
                          : ''
                      }
                      helperText={
                        monthsReservesComputed != null && (field.value == null || field.value === '')
                          ? 'Leave blank to use the Vesta-computed value as the pricing fallback'
                          : 'Used by the pricing engine'
                      }
                      sx={{ minWidth: 220 }}
                    />
                  )}
                />
              </Stack>
            ) : (
              <Stack spacing={0.25} sx={{ maxWidth: 360, textAlign: 'right' }}>
                {monthsReservesEstimateApi != null && monthsReservesEstimateApi !== '' && (
                  <Typography variant="body2" fontWeight={600}>
                    Estimate saved: {formatMonths(monthsReservesEstimateApi)} mo
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {monthsReservesComputed != null && monthsReservesComputed !== ''
                    ? 'Vesta computes months from asset accounts. Your estimate is used for pricing when the computed value is blank.'
                    : 'Number of months the borrower can cover housing expenses using their reserves. Used by the pricing engine.'}
                </Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Assets</Typography>
      {assets.length === 0 && !isEditMode ? (
        <Typography variant="body2" color="text.secondary">No assets recorded for this loan.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Institution</TableCell>
              <TableCell>Account</TableCell>
              <TableCell align="right">Value</TableCell>
              {isEditMode && <TableCell sx={{ width: 40 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((a, i) => (
              <TableRow key={a.assetId || i}>
                {isEditMode ? (
                  <>
                    <TableCell>
                      <Controller
                        name={`assets.${i}.assetType`}
                        control={control}
                        render={({ field }) => (
                          <TextField select size="small" value={field.value || ''} onChange={(e) => field.onChange(e.target.value)} sx={{ minWidth: 120 }}>
                            <MenuItem value="">Select...</MenuItem>
                            {ASSET_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                          </TextField>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`assets.${i}.institutionName`}
                        control={control}
                        render={({ field }) => (
                          <TextField size="small" value={field.value || ''} onChange={(e) => field.onChange(e.target.value)} placeholder="Bank name" />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`assets.${i}.accountIdentifier`}
                        control={control}
                        render={({ field }) => (
                          <TextField size="small" value={field.value || ''} onChange={(e) => field.onChange(e.target.value)} placeholder="Last 4" />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`assets.${i}.assetValue`}
                        control={control}
                        render={({ field }) => (
                          <CurrencyInput size="small" prefix="" value={field.value ?? ''} onChange={(v) => field.onChange(typeof v === 'number' ? v : null)} placeholder="0" sx={{ '& input': { textAlign: 'right' } }} />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => {
                        const updated = assets.filter((_, idx) => idx !== i);
                        updateAssets(updated);
                      }} title="Remove asset">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell><Chip label={ASSET_TYPES.find(t => t.value === a.assetType)?.label || a.assetType || '—'} size="small" variant="outlined" color="info" /></TableCell>
                    <TableCell>{a.institutionName || '—'}</TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{a.accountIdentifier || '—'}</Typography></TableCell>
                    <TableCell align="right">{fmt(a.assetValue)}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={isEditMode ? 3 : 3}><Typography fontWeight={700}>Total Assets</Typography></TableCell>
              <TableCell align="right"><Typography fontWeight={700} color="success.main">{fmt(totalAssets)}</Typography></TableCell>
              {isEditMode && <TableCell />}
            </TableRow>
          </TableFooter>
        </Table>
      )}
      {isEditMode && (
        <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => {
          const newAsset = { assetType: '', institutionName: '', accountIdentifier: '', assetValue: null, description: '' };
          updateAssets([...assets, newAsset]);
        }}>Add Asset</Button>
      )}

        </Grid>

        {/* Right column: Asset summary sidebar (sticky) */}
        {showSidebar && (
          <Grid size={{ xs: 12, lg: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
            {assetSidebar}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AssetsTab;
